"use client";

import Link from "next/link";
import Image from "next/image";
import {
  FaFacebookF,
  FaLinkedinIn,
  FaInstagram,
  FaYoutube,
} from "react-icons/fa";
import { FaTiktok, FaXTwitter } from "react-icons/fa6";
import { useEffect, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { usePathname } from "next/navigation";
import { useSections } from "../hooks/useSections";
import { useSiteSettings } from "../hooks/useSiteSettings";
import { resolveLocalizedValue } from "../utils/localization";
import { getClientRegion, getRegionLocaleHref } from "@/services/region";
import SiteLogo from "@/components/SiteLogo";

export default function Footer() {
  const locale = useLocale();
  const t = useTranslations("web.footer");
  const pathname = usePathname();
  const regionSlug = getClientRegion(pathname) || undefined;
  const { sections } = useSections(locale, regionSlug);
  const settings = useSiteSettings();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const getLocalizedHref = (path: string) => {
    return getRegionLocaleHref(pathname, locale, path);
  };

  const socialLinks = [
    {
      key: "facebook",
      href: settings?.social.facebook,
      icon: <FaFacebookF />,
      label: "Facebook",
    },
    {
      key: "instagram",
      href: settings?.social.instagram,
      icon: <FaInstagram />,
      label: "Instagram",
    },
    {
      key: "linkedin",
      href: settings?.social.linkedin,
      icon: <FaLinkedinIn />,
      label: "LinkedIn",
    },
    {
      key: "x",
      href: settings?.social.x,
      icon: <FaXTwitter />,
      label: "X",
    },
    {
      key: "youtube",
      href: settings?.social.youtube,
      icon: <FaYoutube />,
      label: "YouTube",
    },
    {
      key: "tiktok",
      href: settings?.social.tiktok,
      icon: <FaTiktok />,
      label: "TikTok",
    },
  ].filter(
    (socialLink): socialLink is typeof socialLink & { href: string } =>
      mounted &&
      typeof socialLink.href === "string" &&
      socialLink.href.trim().length > 0,
  );

  return (
    <footer className="relative py-5 mt-5 bg-footer pt-16 pb-4 overflow-visible">
      {" "}
      <div className="relative max-w-[1200px] mx-auto px-6">
        {" "}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-3 gap-10 px-4 sm:px-0">
            <div>
              <h4 className="font-semibold text-[18px] !text-info mb-6">
                {t("course_categories")}
              </h4>
              <ul className="p-0 m-0 list-none text-[17px] space-y-2">
                {sections.map((section) => (
                  <li key={section.id}>
                    <Link
                      href={getLocalizedHref(`/categories/${section.slug}`)}
                      className="!text-on-footer hover:!text-secondary cursor-pointer"
                    >
                      {resolveLocalizedValue(section.name, locale)}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-[18px] !text-info mb-6">
                {t("our_story")}
              </h4>
              <ul className="p-0 m-0 list-none text-[17px] space-y-2">
                <li>
                  <Link
                    href={getLocalizedHref("/about-us")}
                    className="!text-on-footer hover:!text-secondary cursor-pointer"
                  >
                    {t("about_us")}
                  </Link>
                </li>
                <li>
                  <Link
                    href={getLocalizedHref("/terms-of-service")}
                    className="!text-on-footer hover:!text-secondary cursor-pointer"
                  >
                    {t("terms_of_service")}
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-[18px] !text-info mb-6">
                {t("more")}
              </h4>
              <ul className="p-0 m-0 list-none text-[17px] space-y-2">
                <li>
                  <Link
                    href={getLocalizedHref("/how-to-join-ijaazah")}
                    className="!text-on-footer hover:!text-secondary cursor-pointer"
                  >
                    {t("how_to_join")}
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="md:col-span-4 flex flex-col items-center text-center">
            <SiteLogo
              variant="light"
              fallbackSrc="/f/images/logo-white.png"
              width={100}
              height={150}
              style={{ height: "auto" }}
              className="mb-6"
            />

            {socialLinks.length > 0 ? (
              <div className="mt-4 flex gap-6 text-xl text-on-footer">
                {socialLinks.map((socialLink) => (
                  <a
                    key={socialLink.key}
                    href={socialLink.href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={socialLink.label}
                    className="hover:text-secondary transition cursor-pointer"
                  >
                    {socialLink.icon}
                  </a>
                ))}
              </div>
            ) : null}
          </div>
        </div>
        <div className="mt-3 text-center text-on-footer text-[14px]">
          {t("copyright_text", { year: new Date().getFullYear() })}{" "}
          <Link
            href={getLocalizedHref("/")}
            className="underline font-medium !text-secondary cursor-pointer"
          >
            {mounted && settings?.site.name ? settings.site.name : t("fallback_site_name")}
          </Link>
        </div>
      </div>
      {/* height matches the asset's 466x506 aspect ratio at width 250 - the
          browser already rendered it at 271px, so declaring 250 here only
          under-reserved space and triggered next/image's aspect-ratio warning. */}
      <Image
        src="/f/images/footerimg.png"
        alt="Footer Decoration"
        width={250}
        height={271}
        className="absolute bottom-0 right-0 z-5 pointer-events-none"
      />
    </footer>
  );
}
